import { useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { SiteContentContext } from "../context/SiteContentContext";
import { UserContext } from "../context/UserContext";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from "../hooks/useLocalePath";

const LOCALE_STORAGE_KEY = "asm-locale";

const LOCALE_TO_OG = {
  sv: "sv_SE",
  en: "en_US",
};

export default function LocaleLayout() {
  const { locale } = useParams();
  const { setLocale } = useContext(UserContext);
  const { languages } = useContext(SiteContentContext);
  const location = useLocation();

  useEffect(() => {
    if (SUPPORTED_LOCALES.includes(locale)) {
      setLocale(locale);
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      } catch {
        // localStorage unavailable
      }
    }
  }, [locale, setLocale]);

  if (!SUPPORTED_LOCALES.includes(locale)) {
    // The :locale segment matched a non-locale path — e.g. an old, un-prefixed URL like
    // /catalogue or /machines/123 that search engines and backlinks still point at. Treat
    // the whole path as un-localized and send it to the default locale, *preserving the deep
    // link* instead of dumping the visitor on the homepage.
    return (
      <Navigate
        to={`/${DEFAULT_LOCALE}${location.pathname}${location.search}${location.hash}`}
        replace
      />
    );
  }

  const pathWithoutLocale = location.pathname.replace(`/${locale}`, "") || "/";
  const ogLocale = LOCALE_TO_OG[locale] || "sv_SE";
  // Only advertise hreflang alternates for languages that are actually enabled (same source
  // the sitemap uses). With a single live locale we emit none — there's no alternate to point at.
  const enabledLocales = (languages || []).map((l) => l.code).filter(Boolean);

  return (
    <>
      <Helmet>
        <meta property="og:locale" content={ogLocale} />
        <html lang={locale} />
        <link rel="canonical" href={`https://www.asmaskiner.se/${locale}${pathWithoutLocale}`} />
        {enabledLocales.length > 1 &&
          enabledLocales.map((alt) => (
            <link
              key={alt}
              rel="alternate"
              hrefLang={alt}
              href={`https://www.asmaskiner.se/${alt}${pathWithoutLocale}`}
            />
          ))}
        {enabledLocales.length > 1 && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`https://www.asmaskiner.se/${DEFAULT_LOCALE}${pathWithoutLocale}`}
          />
        )}
      </Helmet>
      <Outlet />
    </>
  );
}
